/**
 * Animated Tiles block.
 *
 * Inline Cards: auto-focuses one tile at a time on a configurable interval.
 * Scroll Jacking: pins the intro and scrubs cards along a diagonal as the
 * page scrolls (prototype #processScroll). Falls back to Inline Cards on
 * small screens and prefers-reduced-motion.
 *
 * The active tile shows its animated image (GIF, restarted on activation);
 * inactive tiles show the static image. Hovering/focusing a tile pauses
 * rotation in Inline mode. Numbered nav dots drive the single-tile view
 * on mobile.
 */

const MIN_INTERVAL = 1500;
const SCRUB_MAX_WIDTH = 860;
const SCRUB_DX = 190;
const SCRUB_DY = 155;
const SCRUB_EASE = 0.13;
const SCRUB_DWELL = 0.25;
// Hold card 1 at rest until the cluster has reached viewport center,
// then keep it there for this fraction of the remaining pin-scroll.
const SCRUB_LEAD = 0.3;

export function initAnimatedTiles() {
	document.querySelectorAll( '[data-block="animated-tiles"]' ).forEach( initBlock );
}

/**
 * Wire up a single animated-tiles block.
 *
 * @param {HTMLElement} block
 */
function initBlock( block ) {
	const tiles = Array.from( block.querySelectorAll( '.animated-tiles__tile' ) );
	const dots = Array.from( block.querySelectorAll( '.animated-tiles__dot' ) );
	const nav = block.querySelector( '.animated-tiles__nav' );
	const toggle = block.querySelector( '.animated-tiles__toggle' );
	const pauseIcon = block.querySelector( '.animated-tiles__toggle-icon--pause' );
	const playIcon = block.querySelector( '.animated-tiles__toggle-icon--play' );
	const pinTrack = block.querySelector( '.animated-tiles__pin-track' );
	const isScrollLayout = block.dataset.layout === 'scroll';

	if ( ! tiles.length ) {
		return;
	}

	const parsedInterval = parseInt( block.dataset.interval, 10 );
	const autoEnabled = ! Number.isNaN( parsedInterval ) && parsedInterval > 0;
	const interval = Math.max( MIN_INTERVAL, parsedInterval || 0 );
	const reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
	const smallMq = window.matchMedia( `(max-width: ${ SCRUB_MAX_WIDTH }px)` );

	let current = tiles.findIndex( ( t ) => t.classList.contains( 'is-active' ) );
	if ( current < 0 ) {
		current = 0;
	}

	let timer = null;
	let userPaused = false;
	let interactionPaused = false;
	let inView = true;
	let scrubbing = false;
	let scrubTarget = 0;
	let scrubCurrent = 0;
	let scrubRunning = false;
	let lastScrubActive = current;

	/**
	 * Restart a tile's animated GIF so it plays from the first frame.
	 *
	 * @param {HTMLElement} tile
	 */
	function restartGif( tile ) {
		const gif = tile.querySelector( '.animated-tiles__img--animated' );
		if ( ! gif ) {
			return;
		}
		const src = gif.dataset.gifSrc || gif.getAttribute( 'src' );
		if ( ! src ) {
			return;
		}
		gif.src = '';
		gif.src = src;
	}

	/**
	 * Activate a tile by index.
	 *
	 * @param {number}  index
	 * @param {boolean} restart Whether to restart the GIF (default true).
	 */
	function setActive( index, restart = true ) {
		current = ( index + tiles.length ) % tiles.length;

		tiles.forEach( ( tile, i ) => {
			const active = i === current;
			tile.classList.toggle( 'is-active', active );
			if ( active && restart ) {
				restartGif( tile );
			}
		} );

		dots.forEach( ( dot, i ) => {
			const active = i === current;
			dot.classList.toggle( 'is-active', active );
			dot.setAttribute( 'aria-selected', active ? 'true' : 'false' );
			dot.setAttribute( 'tabindex', active ? '0' : '-1' );
		} );
	}

	function advance() {
		setActive( current + 1 );
	}

	function canAutoRotate() {
		return (
			autoEnabled &&
			! reduceMotion &&
			! userPaused &&
			! interactionPaused &&
			! scrubbing &&
			inView &&
			! document.hidden &&
			tiles.length > 1
		);
	}

	function start() {
		if ( ! canAutoRotate() ) {
			return;
		}
		stop();
		timer = window.setInterval( advance, interval );
	}

	function stop() {
		if ( timer ) {
			window.clearInterval( timer );
			timer = null;
		}
	}

	function setUserPaused( paused ) {
		userPaused = paused;
		block.classList.toggle( 'is-paused', paused );

		if ( toggle ) {
			const pauseLabel = toggle.dataset.labelPause || 'Pause tile rotation';
			const playLabel = toggle.dataset.labelPlay || 'Play tile rotation';
			toggle.setAttribute( 'aria-pressed', paused ? 'true' : 'false' );
			toggle.setAttribute( 'aria-label', paused ? playLabel : pauseLabel );
		}

		if ( pauseIcon && playIcon ) {
			pauseIcon.toggleAttribute( 'hidden', paused );
			playIcon.toggleAttribute( 'hidden', ! paused );
		}

		if ( paused ) {
			stop();
		} else {
			start();
		}
	}

	function shouldScrub() {
		return isScrollLayout && ! reduceMotion && ! smallMq.matches;
	}

	function clearScrubStyles() {
		tiles.forEach( ( tile ) => {
			tile.style.transform = '';
			tile.style.opacity = '';
			tile.style.zIndex = '';
		} );
	}

	function focusEase( p ) {
		const n = tiles.length;
		if ( n < 2 ) {
			return p;
		}
		const seg = 1 / ( n - 1 );
		const idx = Math.min( n - 2, Math.floor( p / seg ) );
		const local = ( p - idx * seg ) / seg;
		const smooth = local * local * local * ( local * ( local * 6 - 15 ) + 10 );
		const blended = local + ( smooth - local ) * SCRUB_DWELL;
		return idx + blended;
	}

	function renderScrub( p ) {
		const activeFloat = focusEase( p );
		let newActive = current;

		tiles.forEach( ( tile, i ) => {
			const d = i - activeFloat;
			const ad = Math.abs( d );
			const x = d * SCRUB_DX;
			const y = d * SCRUB_DY;
			const s = Math.max( 0.72, 1 - ad * 0.16 );
			const op = Math.max( 0.12, 1 - ad * 0.55 );
			tile.style.transform = `translate(-50%,-50%) translate(${ x }px,${ y }px) scale(${ s })`;
			tile.style.opacity = String( op );
			tile.style.zIndex = String( 100 - Math.round( ad * 20 ) );
			const active = ad < 0.5;
			tile.classList.toggle( 'is-active', active );
			if ( active ) {
				newActive = i;
			}
		} );

		if ( newActive !== lastScrubActive ) {
			lastScrubActive = newActive;
			current = newActive;
			restartGif( tiles[ newActive ] );
			dots.forEach( ( dot, i ) => {
				const active = i === newActive;
				dot.classList.toggle( 'is-active', active );
				dot.setAttribute( 'aria-selected', active ? 'true' : 'false' );
				dot.setAttribute( 'tabindex', active ? '0' : '-1' );
			} );
		}
	}

	function readScrubTarget() {
		if ( ! pinTrack ) {
			return;
		}

		const vh = window.innerHeight;
		const header = Number.parseFloat(
			getComputedStyle( document.documentElement ).getPropertyValue( '--header-height' )
		) || 75;
		const viewCenter = header + ( vh - header ) * 0.5;
		const cardsEl = block.querySelector( '.animated-tiles__track' );
		const cardsRect = ( cardsEl || pinTrack ).getBoundingClientRect();
		const clusterMid = cardsRect.top + cardsRect.height / 2;

		// Keep the first card active until the cluster is about at viewport center.
		if ( clusterMid > viewCenter + 8 ) {
			scrubTarget = 0;
			return;
		}

		const rect = pinTrack.getBoundingClientRect();
		const total = rect.height - vh;
		const raw = total > 0 ? -rect.top / total : 0;
		const p = Math.max( 0, Math.min( 1, raw ) );
		scrubTarget = p <= SCRUB_LEAD ? 0 : ( p - SCRUB_LEAD ) / ( 1 - SCRUB_LEAD );
	}

	function scrubLoop() {
		const diff = scrubTarget - scrubCurrent;
		if ( Math.abs( diff ) < 0.0004 ) {
			scrubCurrent = scrubTarget;
			renderScrub( scrubCurrent );
			scrubRunning = false;
			return;
		}
		scrubCurrent += diff * SCRUB_EASE;
		renderScrub( scrubCurrent );
		requestAnimationFrame( scrubLoop );
	}

	function kickScrub() {
		if ( ! scrubbing ) {
			return;
		}
		readScrubTarget();
		if ( ! scrubRunning ) {
			scrubRunning = true;
			requestAnimationFrame( scrubLoop );
		}
	}

	function applyMode() {
		const nextScrub = shouldScrub();
		if ( nextScrub === scrubbing ) {
			if ( scrubbing ) {
				kickScrub();
			}
			return;
		}

		scrubbing = nextScrub;
		block.classList.toggle( 'is-scrubbing', scrubbing );

		if ( scrubbing ) {
			stop();
			if ( toggle ) {
				toggle.hidden = true;
			}
			readScrubTarget();
			scrubCurrent = scrubTarget;
			lastScrubActive = current;
			renderScrub( scrubCurrent );
		} else {
			scrubRunning = false;
			clearScrubStyles();
			if ( toggle && autoEnabled && ! reduceMotion && tiles.length > 1 ) {
				toggle.hidden = false;
			}
			setActive( current, false );
			start();
		}
	}

	if ( toggle && ( ! autoEnabled || reduceMotion ) ) {
		toggle.hidden = true;
	}

	if ( toggle ) {
		toggle.addEventListener( 'click', () => {
			setUserPaused( ! userPaused );
		} );
	}

	tiles.forEach( ( tile ) => {
		const index = parseInt( tile.dataset.index, 10 );

		tile.addEventListener( 'mouseenter', () => {
			if ( scrubbing ) {
				return;
			}
			interactionPaused = true;
			stop();
			setActive( index );
		} );

		tile.addEventListener( 'mouseleave', () => {
			if ( scrubbing ) {
				return;
			}
			interactionPaused = false;
			start();
		} );

		tile.addEventListener( 'focusin', () => {
			if ( scrubbing ) {
				return;
			}
			interactionPaused = true;
			stop();
			setActive( index );
		} );

		tile.addEventListener( 'focusout', ( e ) => {
			if ( scrubbing || tile.contains( e.relatedTarget ) ) {
				return;
			}
			interactionPaused = false;
			start();
		} );
	} );

	dots.forEach( ( dot, dotIndex ) => {
		const index = parseInt( dot.dataset.index, 10 );
		dot.addEventListener( 'click', () => {
			if ( scrubbing ) {
				return;
			}
			setActive( index );
			stop();
			start();
		} );

		dot.addEventListener( 'keydown', ( event ) => {
			if ( scrubbing ) {
				return;
			}
			let nextIndex = null;

			switch ( event.key ) {
				case 'ArrowLeft':
					nextIndex = ( dotIndex - 1 + dots.length ) % dots.length;
					break;
				case 'ArrowRight':
					nextIndex = ( dotIndex + 1 ) % dots.length;
					break;
				case 'Home':
					nextIndex = 0;
					break;
				case 'End':
					nextIndex = dots.length - 1;
					break;
				default:
					return;
			}

			event.preventDefault();
			setActive( nextIndex );
			dots[ nextIndex ].focus();
			stop();
			start();
		} );
	} );

	if ( nav ) {
		nav.addEventListener( 'focusin', () => {
			if ( scrubbing ) {
				return;
			}
			interactionPaused = true;
			stop();
		} );

		nav.addEventListener( 'focusout', ( event ) => {
			if ( scrubbing || nav.contains( event.relatedTarget ) ) {
				return;
			}
			interactionPaused = false;
			start();
		} );
	}

	document.addEventListener( 'visibilitychange', () => {
		if ( document.hidden ) {
			stop();
		} else {
			start();
		}
	} );

	if ( 'IntersectionObserver' in window ) {
		const observer = new IntersectionObserver(
			( entries ) => {
				entries.forEach( ( entry ) => {
					inView = entry.isIntersecting;
					if ( inView ) {
						start();
					} else {
						stop();
					}
				} );
			},
			{ threshold: 0.15 }
		);
		observer.observe( block );
	}

	if ( isScrollLayout ) {
		window.addEventListener( 'scroll', kickScrub, { passive: true } );
		window.addEventListener( 'resize', applyMode );
		if ( typeof smallMq.addEventListener === 'function' ) {
			smallMq.addEventListener( 'change', applyMode );
		} else if ( typeof smallMq.addListener === 'function' ) {
			smallMq.addListener( applyMode );
		}
		applyMode();
	}

	if ( ! scrubbing ) {
		setActive( current, true );
		start();
	}
}
